import { Tooltip, Typography } from '@recruit-robin/robin-theme/web-components';
import {
  Indeed,
  Linkedin,
  LinkedinFill,
  Mail_01,
  RobinIconMonoFulfilled,
  Whatsapp,
} from '@recruit-robin/robin-theme/web-icons';
import { routes } from 'config/routes';
import { CommunicationChannel, useCommunicationChannelsStatus } from 'hooks/shared/useCommunicationChannelsStatus';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useStyles } from './CommunicationChannelsDots.styles';
import { CommunicationChannelsDotsProps } from './CommunicationChannelsDots.types';

export const CommunicationChannelsDots = ({ className, disableChannels = [] }: CommunicationChannelsDotsProps) => {
  const channelsStatus = useCommunicationChannelsStatus();
  const classes = useStyles();
  const navigate = useNavigate();
  const { dictionary } = useContext(LocalizationContext);

  const channels: {
    id: CommunicationChannel;
    icon: React.ReactNode;
    isConnected: boolean;
    onClick: () => void;
  }[] = [
    {
      id: 'email',
      icon: (
        <Mail_01
          size={14}
          color={(c) => (channelsStatus.email.isConnected ? c.secondary.sky[60] : c.neutral.gray[40])}
        />
      ),
      isConnected: channelsStatus.email.isConnected,
      onClick: () => {
        if (!channelsStatus.email.isConnected) {
          navigate(routes.emailSettings);
        }
      },
    },
    {
      id: 'whatsapp',
      icon: (
        <Whatsapp
          size={14}
          color={(c) => (channelsStatus.whatsapp.isConnected ? c.secondary.sky[60] : c.neutral.gray[40])}
        />
      ),
      isConnected: channelsStatus.whatsapp.isConnected,
      onClick: () => {
        return;
      },
    },
    {
      id: 'linkedin',
      icon: (
        <Linkedin
          size={12}
          color={(c) => (channelsStatus.linkedin.isConnected ? c.secondary.sky[60] : c.neutral.gray[40])}
        />
      ),
      isConnected: channelsStatus.linkedin.isConnected,
      onClick: () => {
        if (!channelsStatus.linkedin.isConnected) {
          navigate(routes.browserExtension);
        }
      },
    },
    {
      id: 'linkedinInmail',
      icon: (
        <LinkedinFill
          size={14}
          color={(c) => (channelsStatus.linkedinInmail.isConnected ? c.secondary.sky[60] : c.neutral.gray[40])}
        />
      ),
      isConnected: channelsStatus.linkedinInmail.isConnected,
      onClick: () => {
        if (!channelsStatus.linkedinInmail.isConnected) {
          navigate(routes.browserExtension);
        }
      },
    },
    {
      id: 'indeed',
      icon: (
        <Indeed
          size={14}
          color={(c) => (channelsStatus.indeed.isConnected ? c.secondary.sky[60] : c.neutral.gray[40])}
        />
      ),
      isConnected: channelsStatus.indeed.isConnected,
      onClick: () => {
        if (!channelsStatus.indeed.isConnected) {
          navigate(routes.browserExtension);
        }
      },
    },
    {
      id: 'rjCommunicationHub',
      icon: (
        <RobinIconMonoFulfilled
          size={14}
          color={(c) => (channelsStatus.rjCommunicationHub.isConnected ? c.secondary.sky[60] : c.neutral.gray[40])}
        />
      ),
      isConnected: channelsStatus.rjCommunicationHub.isConnected,
      onClick: () => {
        return;
      },
    },
  ];

  const connectedLabels: Record<CommunicationChannel, string> = {
    email: dictionary.inboxEmailConnected,
    whatsapp: dictionary.inboxWhatsappConnected,
    linkedin: dictionary.inboxLinkedinConnected,
    linkedinInmail: dictionary.inboxLinkedinInmailConnected,
    indeed: dictionary.inboxIndeedConnected,
    rjCommunicationHub: dictionary.inboxRjCommunicationHubConnected,
  };

  const disconnectedLabels: Record<CommunicationChannel, string> = {
    email: dictionary.inboxEmailDisconnected,
    whatsapp: dictionary.inboxWhatsappDisconnected,
    linkedin: dictionary.inboxLinkedinDisconnected,
    linkedinInmail: dictionary.inboxLinkedinInmailDisconnected,
    indeed: dictionary.inboxIndeedDisconnected,
    rjCommunicationHub: dictionary.inboxRjCommunicationHubDisconnected,
  };

  return (
    <div css={classes.root} className={className}>
      {channels
        .filter((channel) => !disableChannels.includes(channel.id))
        .sort((a, b) => (b.isConnected ? 1 : -1))
        .map((channel, idx) => (
          <Tooltip
            key={channel.id}
            offset={5}
            customCss={classes.tooltip}
            label={
              <Typography variant={(v) => v.supporting.helper} customCss={classes.tooltipSize}>
                {channel.isConnected ? connectedLabels[channel.id] : disconnectedLabels[channel.id]}
              </Typography>
            }>
            <div
              css={[classes.dot, channel.isConnected ? classes.dotActive : classes.dotInactive]}
              style={{ zIndex: channels.length - idx }}
              onClick={channel.onClick}>
              {channel.icon}
            </div>
          </Tooltip>
        ))}
    </div>
  );
};
